import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { documentsAPI } from '../services/api';
import { CATEGORIES, PLACEHOLDER_HINTS } from '../components/documents/documentTemplateConstants';
import DocumentTemplatesGrid from '../components/documents/DocumentTemplatesGrid';
import DocumentTemplateEditorDialog from '../components/documents/DocumentTemplateEditorDialog';

const DocumentTemplates = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filterCategory, setFilterCategory] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [showVariables, setShowVariables] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'administratif',
    html_body: '',
    css: '',
    is_active: true,
  });

  const templatesQuery = useQuery({
    queryKey: ['document-templates'],
    queryFn: () => documentsAPI.listTemplates({ include_inactive: true }),
  });

  useEffect(() => {
    if (templatesQuery.isError) toast.error(t('documentTemplates.toasts.loadError'));
  }, [templatesQuery.isError]);

  const templates = templatesQuery.data ?? [];
  const loading = templatesQuery.isPending;

  const handleCreate = () => {
    setCurrentTemplate(null);
    setFormData({ name: '', description: '', category: 'administratif', html_body: '', css: '', is_active: true });
    setEditorOpen(true);
  };

  const handleEdit = (template) => {
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category || 'administratif',
      html_body: template.html_body,
      css: template.css || '',
      is_active: template.is_active,
    });
    setEditorOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      await documentsAPI.createTemplate({
        name: `${template.name} (Copie)`,
        description: template.description,
        category: template.category,
        html_body: template.html_body,
        css: template.css,
        placeholders_hint: template.placeholders_hint,
        is_active: true,
      });
      toast.success(t('documentTemplates.toasts.duplicateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    } catch (error) {
      toast.error(t('documentTemplates.toasts.duplicateError'));
    }
  };

  const handleDelete = async (template) => {
    if (template.is_default) {
      toast.error(t('documentTemplates.toasts.defaultCannotDelete'));
      return;
    }
    if (!window.confirm(t('documentTemplates.toasts.deleteConfirm', { name: template.name }))) return;
    try {
      await documentsAPI.deleteTemplate(template.id);
      toast.success(t('documentTemplates.toasts.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('documentTemplates.toasts.deleteError'));
    }
  };

  const validateTemplate = () => {
    const varPattern = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*(?:\|[^}]*)?\}\}/g;
    const usedVars = new Set();
    let match;
    while ((match = varPattern.exec(formData.html_body)) !== null) {
      usedVars.add(match[1].split('|')[0].trim());
    }
    const validRoots = ['employee', 'company', 'generated_at', 'generated_by', 'options'];
    const invalidVars = [...usedVars].filter((v) => !validRoots.includes(v.split('.')[0]));
    return { isValid: invalidVars.length === 0, invalidVars };
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error(t('documentTemplates.toasts.nameRequired')); return; }
    if (!formData.html_body.trim()) { toast.error(t('documentTemplates.toasts.contentRequired')); return; }

    const validation = validateTemplate();
    if (!validation.isValid) {
      const invalidList = validation.invalidVars.map((v) => `{{ ${v} }}`).join(', ');
      toast.error(
        t('documentTemplates.toasts.invalidVariables', { vars: invalidList }),
        { duration: 6000 }
      );
      return;
    }

    try {
      const payload = { ...formData, placeholders_hint: PLACEHOLDER_HINTS };
      if (currentTemplate) {
        await documentsAPI.updateTemplate(currentTemplate.id, payload);
        toast.success(t('documentTemplates.toasts.updateSuccess'));
      } else {
        await documentsAPI.createTemplate(payload);
        toast.success(t('documentTemplates.toasts.createSuccess'));
      }
      setEditorOpen(false);
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || t('documentTemplates.toasts.saveError');
      if (errorDetail.includes('❌')) {
        toast.error(errorDetail, { duration: 8000, style: { whiteSpace: 'pre-line' } });
      } else {
        toast.error(errorDetail);
      }
    }
  };

  const filteredTemplates = templates.filter((t) => filterCategory === 'all' || t.category === filterCategory);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 sm:text-3xl">{t('documentTemplates.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('documentTemplates.subtitle')}</p>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {t('documentTemplates.newTemplate')}
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium text-muted-foreground">{t('documentTemplates.category')}</Label>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant={filterCategory === 'all' ? 'default' : 'outline'} onClick={() => setFilterCategory('all')}>
                {t('documentTemplates.all')}
              </Button>
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.value}
                  size="sm"
                  variant={filterCategory === cat.value ? 'default' : 'outline'}
                  onClick={() => setFilterCategory(cat.value)}
                >
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <DocumentTemplatesGrid
        loading={loading}
        filteredTemplates={filteredTemplates}
        handleEdit={handleEdit}
        handleDuplicate={handleDuplicate}
        handleDelete={handleDelete}
      />

      <DocumentTemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        currentTemplate={currentTemplate}
        formData={formData}
        setFormData={setFormData}
        handleSave={handleSave}
        handleDuplicate={handleDuplicate}
        showVariables={showVariables}
        setShowVariables={setShowVariables}
      />
    </div>
  );
};

export default DocumentTemplates;
